/**
 * Lumo Accessibility Widget
 * Drop-in accessibility overlay for any website.
 * Usage: <script src="YOUR_DOMAIN/widget.js" data-color="#0057ff" data-position="left" defer></script>
 */
(function () {
  'use strict';

  const config = (function () {
    const script = document.currentScript || document.querySelector('script[src*="widget.js"]');
    const src = (script && script.getAttribute('src')) || '';
    const baseUrl = src ? src.replace(/\/widget\.js.*$/, '') : window.location.origin;
    return {
      color:    (script && script.getAttribute('data-color'))    || '#0057ff',
      position: (script && script.getAttribute('data-position')) || 'left',
      baseUrl,
    };
  })();

  // ── State ──────────────────────────────────────────────────────────────────
  const state = {
    open: false,
    fontSize: 0,
    highContrast: false,
    darkContrast: false,
    grayscale: false,
    invertColors: false,
    highlightLinks: false,
    highlightHeadings: false,
    pauseAnimations: false,
    bigCursor: false,
    textSpacing: false,
    dyslexicFont: false,
    readingGuide: false,
    screenReader: false,
  };

  // pageWrap is set in buildWidget() — filter/visual effects go on this element,
  // NOT on body, so the widget's own fixed-position elements are never affected
  // by CSS filter stacking-context side effects.
  let pageWrap = null;

  // Tracks elements whose font-size was modified by the text-size stepper so
  // we can cleanly restore them without CSS class compounding.
  let _fontScaleEls = [];
  // Original html element font-size in px, captured once before any changes.
  let _origHtmlFontSizePx = 0;

  // ── CSS ─────────────────────────────────────────────────────────────────────
  const css = `
    #ag-widget-root *, #ag-widget-root *::before, #ag-widget-root *::after {
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
    }

    /* Content wrapper — filters go here so the widget's fixed elements are unaffected */
    #ag-page-wrap { width: 100%; }

    #ag-trigger {
      position: fixed;
      bottom: 24px;
      ${config.position === 'left' ? 'left: 24px;' : 'right: 24px;'}
      width: 58px; height: 58px;
      border-radius: 50%;
      background: ${config.color};
      border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 4px 20px rgba(0,0,0,0.30);
      z-index: 2147483647;
      transition: transform 0.2s, box-shadow 0.2s;
      outline: none;
    }
    #ag-trigger:hover { transform: scale(1.08); box-shadow: 0 6px 28px rgba(0,0,0,0.36); }
    #ag-trigger:focus-visible { outline: 3px solid #fff; outline-offset: 3px; }
    #ag-trigger img { display: block; }

    #ag-panel {
      position: fixed;
      bottom: 96px;
      ${config.position === 'left' ? 'left: 24px;' : 'right: 24px;'}
      width: 320px; max-height: 80vh;
      background: #fff; border-radius: 16px;
      box-shadow: 0 16px 48px rgba(0,0,0,0.22);
      z-index: 2147483646;
      overflow: hidden; display: flex; flex-direction: column;
      transform: scale(0.92) translateY(16px);
      opacity: 0; pointer-events: none;
      transition: opacity 0.22s ease, transform 0.22s ease;
    }
    #ag-panel.ag-open {
      opacity: 1; transform: scale(1) translateY(0); pointer-events: all;
    }

    #ag-header {
      background: ${config.color}; color: #fff;
      padding: 16px 18px 14px;
      display: flex; align-items: center; justify-content: space-between;
    }
    #ag-header h2 { margin: 0; font-size: 15px; font-weight: 700; letter-spacing: 0.01em; }
    #ag-header p  { margin: 2px 0 0; font-size: 11px; opacity: 0.85; }
    #ag-close {
      background: rgba(255,255,255,0.2); border: none; border-radius: 50%;
      width: 30px; height: 30px; cursor: pointer; color: #fff; font-size: 18px;
      display: flex; align-items: center; justify-content: center;
      transition: background 0.15s; flex-shrink: 0;
    }
    #ag-close:hover { background: rgba(255,255,255,0.35); }

    #ag-body { overflow-y: auto; padding: 12px; flex: 1; }
    #ag-body::-webkit-scrollbar { width: 4px; }
    #ag-body::-webkit-scrollbar-track { background: transparent; }
    #ag-body::-webkit-scrollbar-thumb { background: #ddd; border-radius: 2px; }

    .ag-section-label {
      font-size: 10px; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.08em; color: #999; margin: 14px 4px 8px;
    }
    .ag-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }

    .ag-btn {
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; gap: 6px; padding: 12px 8px;
      background: #f5f6f8; border: 2px solid transparent; border-radius: 12px;
      cursor: pointer; font-size: 11px; font-weight: 600; color: #333;
      text-align: center; line-height: 1.3;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
      min-height: 76px;
    }
    .ag-btn:hover { background: #eaecf2; }
    .ag-btn.ag-active {
      background: ${config.color}18; border-color: ${config.color}; color: ${config.color};
    }
    .ag-btn svg { width: 22px; height: 22px; }
    .ag-btn.ag-active svg { stroke: ${config.color}; }

    .ag-stepper {
      display: flex; align-items: center; justify-content: space-between;
      background: #f5f6f8; border: 2px solid transparent; border-radius: 12px;
      padding: 10px 14px; gap: 8px;
    }
    .ag-stepper.ag-active { background: ${config.color}18; border-color: ${config.color}; }
    .ag-stepper-label {
      flex: 1; display: flex; flex-direction: column; align-items: center;
      font-size: 11px; font-weight: 600; color: #333; gap: 4px;
    }
    .ag-stepper-label svg { width: 22px; height: 22px; }
    .ag-stepper-btn {
      width: 28px; height: 28px; border-radius: 50%; border: none;
      background: #dde0e8; cursor: pointer; font-size: 18px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      line-height: 1; color: #333; transition: background 0.15s; flex-shrink: 0;
    }
    .ag-stepper-btn:hover { background: #c8ccd8; }
    .ag-stepper-value {
      font-size: 11px; font-weight: 700; color: ${config.color};
      min-width: 36px; text-align: center;
    }

    #ag-reset {
      width: calc(100% - 24px); margin: 4px 12px 12px; padding: 10px;
      background: none; border: 2px solid #e0e0e0; border-radius: 10px;
      font-size: 12px; font-weight: 600; color: #666; cursor: pointer;
      transition: background 0.15s, border-color 0.15s;
    }
    #ag-reset:hover { background: #f5f6f8; border-color: #ccc; }


    /* ── Skip link (screen reader mode) ─────────────────────────── */
    .ag-skip-link {
      position: fixed !important; top: -999px !important; left: 12px !important;
      z-index: 2147483645 !important; background: #000 !important; color: #fff !important;
      padding: 8px 16px !important; border-radius: 6px !important;
      font-size: 14px !important; font-weight: 700 !important; text-decoration: none !important;
    }
    body.ag-sr-mode .ag-skip-link:focus { top: 12px !important; }

    /* ── Reading guide ───────────────────────────────────────────── */
    #ag-reading-guide {
      position: fixed; left: 0; width: 100%; height: 2px;
      background: ${config.color}; opacity: 0.7; pointer-events: none;
      z-index: 2147483644; display: none;
      box-shadow: 0 0 8px ${config.color}80;
    }
    body.ag-reading-guide-on #ag-reading-guide { display: block; }

    /* ── Big cursor (body-level — affects full viewport) ─────────── */
    body.ag-big-cursor * {
      cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><path d="M5 5 L5 30 L12 23 L18 35 L22 33 L16 21 L26 21 Z" fill="black" stroke="white" stroke-width="2"/></svg>') 5 5, auto !important;
    }

    /* ═══════════════════════════════════════════════════════════════
       All filter-based effects go on #ag-page-wrap, NOT body.
       This prevents CSS filter stacking-context from breaking
       the widget's own position:fixed elements.
    ═══════════════════════════════════════════════════════════════ */
    #ag-page-wrap.ag-high-contrast { filter: contrast(150%); }
    #ag-page-wrap.ag-dark-contrast  { filter: invert(1) hue-rotate(180deg); }
    /* Re-invert images/video so photos look natural in dark mode */
    #ag-page-wrap.ag-dark-contrast img,
    #ag-page-wrap.ag-dark-contrast video,
    #ag-page-wrap.ag-dark-contrast canvas,
    #ag-page-wrap.ag-dark-contrast [style*="background-image"] { filter: invert(1) hue-rotate(180deg); }
    #ag-page-wrap.ag-grayscale      { filter: grayscale(100%); }
    #ag-page-wrap.ag-invert         { filter: invert(1); }

    #ag-page-wrap.ag-pause-animations *,
    #ag-page-wrap.ag-pause-animations *::before,
    #ag-page-wrap.ag-pause-animations *::after {
      animation: none !important; transition: none !important;
    }

    #ag-page-wrap.ag-dyslexic * {
      font-family: 'OpenDyslexic', 'Comic Sans MS', cursive !important;
    }
    #ag-page-wrap.ag-text-spacing * {
      letter-spacing: 0.12em !important; word-spacing: 0.22em !important; line-height: 1.8 !important;
    }

    #ag-page-wrap.ag-highlight-links a {
      background-color: #ff0 !important; color: #000 !important;
      text-decoration: underline !important; outline: 2px solid #000 !important;
    }
    #ag-page-wrap.ag-highlight-headings h1,
    #ag-page-wrap.ag-highlight-headings h2,
    #ag-page-wrap.ag-highlight-headings h3,
    #ag-page-wrap.ag-highlight-headings h4,
    #ag-page-wrap.ag-highlight-headings h5,
    #ag-page-wrap.ag-highlight-headings h6 {
      outline: 2px solid ${config.color} !important; outline-offset: 2px !important;
      background: ${config.color}18 !important; padding: 2px 4px !important;
    }

    /* Font scaling is handled in JS (computed-value approach) to avoid
       compounding through the DOM tree. No CSS rules needed here. */
  `;

  // ── Trigger icon (International Symbol of Access) ───────────────────────────
  const triggerIconHtml = `<img src="${config.baseUrl}/accessibility-icon.png" width="32" height="32" alt="" aria-hidden="true" style="display:block;width:32px;height:32px;object-fit:contain;filter:brightness(0) invert(1);">`;

  const icons = {
    contrast: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 3v18" fill="none"/><path d="M12 3a9 9 0 0 1 0 18Z" fill="currentColor" stroke="none"/></svg>`,
    dark:     `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`,
    grayscale:`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><line x1="12" y1="3" x2="12" y2="21"/></svg>`,
    link:     `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`,
    heading:  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 12h16M4 6h16M4 18h10"/></svg>`,
    pause:    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>`,
    cursor:   `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3l14 9-7 1-4 7z"/></svg>`,
    spacing:  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 8h18M3 16h18"/><path d="M8 3l-4 5 4 5M16 3l4 5-4 5"/></svg>`,
    dyslexic: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 19 L8 5 L12 15 L16 5 L20 19"/></svg>`,
    guide:    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="12" x2="21" y2="12"/><path d="M3 6h18M3 18h18"/></svg>`,
    sr:       `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`,
    reset:    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.58"/></svg>`,
  };

  // ── Wrap existing page content ───────────────────────────────────────────────
  // Moves all existing body children into #ag-page-wrap so CSS filters
  // applied to that wrapper don't affect position:fixed widget elements.
  function wrapPageContent() {
    pageWrap = document.createElement('div');
    pageWrap.id = 'ag-page-wrap';
    const frag = document.createDocumentFragment();
    while (document.body.firstChild) {
      frag.appendChild(document.body.firstChild);
    }
    pageWrap.appendChild(frag);
    document.body.appendChild(pageWrap);
  }

  // ── Build DOM ────────────────────────────────────────────────────────────────
  function buildWidget() {
    // 1. Wrap existing content first
    wrapPageContent();

    // 2. Inject styles
    const styleEl = document.createElement('style');
    styleEl.textContent = css;
    document.head.appendChild(styleEl);

    // 3. Reading guide overlay (outside wrapper, not affected by filters)
    const guideEl = document.createElement('div');
    guideEl.id = 'ag-reading-guide';
    document.body.appendChild(guideEl);

    // 4. Skip link (outside wrapper)
    const skipLink = document.createElement('a');
    skipLink.href = '#main-content';
    skipLink.className = 'ag-skip-link';
    skipLink.textContent = 'Skip to main content';
    skipLink.addEventListener('focus', () => { skipLink.style.top = '12px'; });
    skipLink.addEventListener('blur',  () => { skipLink.style.top = '-999px'; });
    document.body.appendChild(skipLink);

    // 5. Widget root (outside wrapper)
    const root = document.createElement('div');
    root.id = 'ag-widget-root';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label', 'Accessibility Options');

    const trigger = document.createElement('button');
    trigger.id = 'ag-trigger';
    trigger.setAttribute('aria-label', 'Open accessibility options');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', 'ag-panel');
    trigger.innerHTML = triggerIconHtml;
    trigger.addEventListener('click', togglePanel);

    const panel = document.createElement('div');
    panel.id = 'ag-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute('aria-label', 'Accessibility Options');

    panel.innerHTML = `
      <div id="ag-header">
        <div>
          <h2>Accessibility Options</h2>
          <p>Powered by Lumo</p>
        </div>
        <button id="ag-close" aria-label="Close accessibility options">✕</button>
      </div>
      <div id="ag-body">

        <div class="ag-section-label">Text</div>
        <div class="ag-stepper" id="ag-font-stepper">
          <button class="ag-stepper-btn" id="ag-font-dec" aria-label="Decrease font size">−</button>
          <div class="ag-stepper-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 20L12 4L20 20"/><path d="M7.5 14h9"/></svg>
            Text Size
            <span class="ag-stepper-value" id="ag-font-val">Normal</span>
          </div>
          <button class="ag-stepper-btn" id="ag-font-inc" aria-label="Increase font size">+</button>
        </div>

        <div class="ag-section-label">Display</div>
        <div class="ag-grid">
          <button class="ag-btn" id="ag-btn-highcontrast" aria-pressed="false">${icons.contrast} High Contrast</button>
          <button class="ag-btn" id="ag-btn-darkcontrast" aria-pressed="false">${icons.dark} Dark Mode</button>
          <button class="ag-btn" id="ag-btn-grayscale" aria-pressed="false">${icons.grayscale} Grayscale</button>
          <button class="ag-btn" id="ag-btn-invertcolors" aria-pressed="false">${icons.contrast} Invert Colors</button>
        </div>

        <div class="ag-section-label">Navigation</div>
        <div class="ag-grid">
          <button class="ag-btn" id="ag-btn-highlightlinks" aria-pressed="false">${icons.link} Highlight Links</button>
          <button class="ag-btn" id="ag-btn-highlightheadings" aria-pressed="false">${icons.heading} Highlight Headings</button>
          <button class="ag-btn" id="ag-btn-readingguide" aria-pressed="false">${icons.guide} Reading Guide</button>
          <button class="ag-btn" id="ag-btn-screenreader" aria-pressed="false">${icons.sr} Skip Links</button>
        </div>

        <div class="ag-section-label">Motion & Content</div>
        <div class="ag-grid">
          <button class="ag-btn" id="ag-btn-pauseanimations" aria-pressed="false">${icons.pause} Pause Animations</button>
          <button class="ag-btn" id="ag-btn-bigcursor" aria-pressed="false">${icons.cursor} Large Cursor</button>
          <button class="ag-btn" id="ag-btn-textspacing" aria-pressed="false">${icons.spacing} Text Spacing</button>
          <button class="ag-btn" id="ag-btn-dyslexicfont" aria-pressed="false">${icons.dyslexic} Dyslexic Font</button>
        </div>

      </div>
      <button id="ag-reset" aria-label="Reset all accessibility settings">↺ Reset All Settings</button>
    `;

    root.appendChild(trigger);
    root.appendChild(panel);
    document.body.appendChild(root);

    // ── Wire events ────────────────────────────────────────────────
    root.querySelector('#ag-close').addEventListener('click', closePanel);
    root.querySelector('#ag-font-inc').addEventListener('click', () => changeFontSize(1));
    root.querySelector('#ag-font-dec').addEventListener('click', () => changeFontSize(-1));
    root.querySelector('#ag-reset').addEventListener('click', resetAll);

    // toggleMap: [selector, stateKey, cssClass, useWrapper]
    // useWrapper=true  → class goes on #ag-page-wrap (filter-based effects)
    // useWrapper=false → class goes on document.body
    const toggleMap = [
      ['#ag-btn-highcontrast',     'highContrast',     'ag-high-contrast',      true],
      ['#ag-btn-darkcontrast',     'darkContrast',     'ag-dark-contrast',      true],
      ['#ag-btn-grayscale',        'grayscale',        'ag-grayscale',          true],
      ['#ag-btn-invertcolors',     'invertColors',     'ag-invert',             true],
      ['#ag-btn-highlightlinks',   'highlightLinks',   'ag-highlight-links',    true],
      ['#ag-btn-highlightheadings','highlightHeadings','ag-highlight-headings', true],
      ['#ag-btn-pauseanimations',  'pauseAnimations',  'ag-pause-animations',   true],
      ['#ag-btn-bigcursor',        'bigCursor',        'ag-big-cursor',         false],
      ['#ag-btn-textspacing',      'textSpacing',      'ag-text-spacing',       true],
      ['#ag-btn-dyslexicfont',     'dyslexicFont',     'ag-dyslexic',           true],
      ['#ag-btn-readingguide',     'readingGuide',     'ag-reading-guide-on',   false],
      ['#ag-btn-screenreader',     'screenReader',     'ag-sr-mode',            false],
    ];

    toggleMap.forEach(([sel, key, cls, useWrapper]) => {
      const btn = root.querySelector(sel);
      btn.addEventListener('click', () => toggle(key, cls, useWrapper, btn));
    });

    // Reading guide mouse tracker
    document.addEventListener('mousemove', (e) => { guideEl.style.top = (e.clientY - 1) + 'px'; });

    // Close on outside click / Escape
    document.addEventListener('click', (e) => { if (state.open && !root.contains(e.target)) closePanel(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && state.open) closePanel(); });

    // Restore persisted settings
    restore(root);
  }

  // ── Panel open/close ─────────────────────────────────────────────────────────
  function togglePanel() { state.open ? closePanel() : openPanel(); }
  function openPanel() {
    state.open = true;
    document.getElementById('ag-panel').classList.add('ag-open');
    document.getElementById('ag-trigger').setAttribute('aria-expanded', 'true');
    document.getElementById('ag-close').focus();
  }
  function closePanel() {
    state.open = false;
    document.getElementById('ag-panel').classList.remove('ag-open');
    document.getElementById('ag-trigger').setAttribute('aria-expanded', 'false');
    document.getElementById('ag-trigger').focus();
  }

  // ── Feature toggle ───────────────────────────────────────────────────────────
  function toggle(stateKey, bodyClass, useWrapper, btn) {
    state[stateKey] = !state[stateKey];
    const target = useWrapper ? pageWrap : document.body;
    target.classList.toggle(bodyClass, state[stateKey]);
    btn.classList.toggle('ag-active', state[stateKey]);
    btn.setAttribute('aria-pressed', state[stateKey].toString());
    persist();
  }

  function _clearFontScale() {
    // Restore root font-size scaling
    if (_origHtmlFontSizePx > 0) {
      document.documentElement.style.removeProperty('font-size');
    }
    // Restore any directly-scaled elements
    _fontScaleEls.forEach(el => el.style.removeProperty('font-size'));
    _fontScaleEls = [];
  }

  function _applyFontScale(scale) {
    // Capture html root size once (before any changes) so restore is clean.
    if (!_origHtmlFontSizePx) {
      _origHtmlFontSizePx = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
    }

    // ── Pass 1: read all natural (unscaled) computed font-sizes FIRST ──────────
    // Must happen before we touch the html element so rem-based sizes are natural.
    const BROAD_SEL = 'h1,h2,h3,h4,h5,h6,p,li,a,button,label,span,div,td,th,figcaption,blockquote';
    const items = [];
    document.querySelectorAll(BROAD_SEL).forEach(el => {
      if (el.closest('#ag-widget-root')) return;
      // Only leaf text-bearers (direct non-empty text node)
      const hasText = Array.from(el.childNodes).some(n => n.nodeType === 3 && n.textContent.trim().length > 0);
      if (!hasText) return;
      const computed = parseFloat(window.getComputedStyle(el).fontSize);
      if (!computed || computed < 8) return;
      items.push({ el, computed });
    });

    // ── Pass 2: apply html root scale (rem-based Shopify themes: Dawn, etc.) ───
    document.documentElement.style.setProperty('font-size', (_origHtmlFontSizePx * scale) + 'px', 'important');

    // ── Pass 3: apply inline px scale to each collected element ─────────────────
    items.forEach(({ el, computed }) => {
      el.style.setProperty('font-size', Math.round(computed * scale * 10) / 10 + 'px', 'important');
      _fontScaleEls.push(el);
    });
  }

  function changeFontSize(delta) {
    const levels = ['Normal', 'Large', 'X-Large', 'Huge'];
    const scales  = [1, 1.12, 1.28, 1.5];
    state.fontSize = Math.max(0, Math.min(3, state.fontSize + delta));

    // Always clear first so we re-read natural (unscaled) computed sizes
    _clearFontScale();
    if (state.fontSize > 0) _applyFontScale(scales[state.fontSize]);

    document.getElementById('ag-font-val').textContent = levels[state.fontSize];
    document.getElementById('ag-font-stepper').classList.toggle('ag-active', state.fontSize > 0);
    persist();
  }

  function resetAll() {
    // Clear JS-applied font scaling first
    _clearFontScale();

    // Remove wrapper classes
    const wrapClasses = ['ag-high-contrast','ag-dark-contrast','ag-grayscale','ag-invert',
      'ag-highlight-links','ag-highlight-headings','ag-pause-animations',
      'ag-text-spacing','ag-dyslexic'];
    wrapClasses.forEach(c => pageWrap.classList.remove(c));

    // Remove body classes
    const bodyClasses = ['ag-big-cursor','ag-reading-guide-on','ag-sr-mode'];
    bodyClasses.forEach(c => document.body.classList.remove(c));

    // Reset state
    Object.keys(state).forEach(k => { if (k !== 'open') state[k] = k === 'fontSize' ? 0 : false; });

    // Reset button UI
    document.querySelectorAll('#ag-widget-root .ag-btn').forEach(b => {
      b.classList.remove('ag-active'); b.setAttribute('aria-pressed', 'false');
    });
    document.getElementById('ag-font-val').textContent = 'Normal';
    document.getElementById('ag-font-stepper').classList.remove('ag-active');
    try { localStorage.removeItem('ag-settings'); } catch(e) {}
  }

  // ── Persistence ──────────────────────────────────────────────────────────────
  function persist() {
    try { localStorage.setItem('ag-settings', JSON.stringify(state)); } catch(e) {}
  }

  function restore(root) {
    try {
      const saved = JSON.parse(localStorage.getItem('ag-settings') || '{}');

      if (saved.fontSize) { state.fontSize = saved.fontSize; changeFontSize(0); }

      const toggleMap = [
        ['highContrast',    'ag-high-contrast',      true],
        ['darkContrast',    'ag-dark-contrast',      true],
        ['grayscale',       'ag-grayscale',          true],
        ['invertColors',    'ag-invert',             true],
        ['highlightLinks',  'ag-highlight-links',    true],
        ['highlightHeadings','ag-highlight-headings',true],
        ['pauseAnimations', 'ag-pause-animations',   true],
        ['bigCursor',       'ag-big-cursor',         false],
        ['textSpacing',     'ag-text-spacing',       true],
        ['dyslexicFont',    'ag-dyslexic',           true],
        ['readingGuide',    'ag-reading-guide-on',   false],
        ['screenReader',    'ag-sr-mode',            false],
      ];

      toggleMap.forEach(([key, cls, useWrapper]) => {
        if (saved[key]) {
          state[key] = true;
          (useWrapper ? pageWrap : document.body).classList.add(cls);
          const btn = root.querySelector(`[id="ag-btn-${key.toLowerCase()}"]`);
          if (btn) { btn.classList.add('ag-active'); btn.setAttribute('aria-pressed', 'true'); }
        }
      });
    } catch(e) {}
  }

  // ── Init ─────────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildWidget);
  } else {
    buildWidget();
  }

})();
